/**
 * Lowest Price Variant Selector
 * 
 * This script:
 * 1. Automatically selects the lowest priced available variant on product pages initially
 * 2. When a user changes an option and the current combination becomes unavailable,
 *    it automatically selects the first available variant within the new option group
 * 3. Updates all condition/grade variant prices when any option changes
 */

// Global mapping of condition names to price elements
let conditionPriceElementsMap = new Map();

document.addEventListener('DOMContentLoaded', function() {
  // Only run on product pages
  if (!window.location.pathname.includes('/products/')) return;
  
  // Find the variant-selects element
  const variantSelects = document.querySelector('variant-selects');
  
  if (!variantSelects) return;
  
  // Get variant data from the JSON script inside variant-selects
  const variantDataElement = variantSelects.querySelector('script[type="application/json"]');
  if (!variantDataElement) return;
  
  try {
    // Parse the variant data
    const variantData = JSON.parse(variantDataElement.textContent);
    
    // Scan the DOM for all price elements and initialize the global mapping
    initializeConditionPriceElements();
    
    // Select lowest price variant initially
    selectLowestPriceVariant(variantSelects, variantData);
    
    // Add event listeners to handle option changes
    setupOptionChangeHandlers(variantSelects, variantData);
    
  } catch (error) {
    console.error('Error in variant selector script:', error);
  }
});

/**
 * Scans the DOM for all price elements and initializes the global mapping
 */
function initializeConditionPriceElements() {
  // Find all elements that might contain variant prices
  const allElements = document.querySelectorAll('*');
  
  for (const element of allElements) {
    // Check if the element has any class containing "variant-price"
    if (element.className && element.className.includes && element.className.includes('variant-price')) {
      // Extract the condition name from the class
      const classList = Array.from(element.classList);
      const priceClass = classList.find(cls => cls.includes('variant-price-'));
      
      if (priceClass) {
        // Extract condition name from the class
        let conditionName = priceClass.replace('variant-price-', '');
        
        // Handle special cases with dots
        conditionName = conditionName.replace(/\./g, ' ');
        
        // Store in the global mapping
        conditionPriceElementsMap.set(conditionName, element);
      }
    }
  }
  

}

/**
 * Selects the lowest priced available variant
 */
function selectLowestPriceVariant(variantSelects, variantData) {
  // Find the lowest priced available variant
  let lowestPriceVariant = null;
  let lowestPrice = Infinity;
  
  for (const variant of variantData) {
    if (variant.available && variant.price < lowestPrice) {
      lowestPrice = variant.price;
      lowestPriceVariant = variant;
    }
  }
  
  if (!lowestPriceVariant) return;
  
  // Handle button-style variant selectors
  const fieldsets = variantSelects.querySelectorAll('fieldset');
  if (fieldsets.length > 0) {
    // Set each option to match the lowest price variant
    fieldsets.forEach((fieldset, index) => {
      const optionValue = lowestPriceVariant.options[index];
      const input = fieldset.querySelector(`input[value="${optionValue}"]`);
      
      if (input && !input.checked) {
        // Select the input
        input.checked = true;
        // Trigger change event to update the UI
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
  
  // Handle dropdown-style variant selectors
  const selects = variantSelects.querySelectorAll('select');
  if (selects.length > 0) {
    selects.forEach((select, index) => {
      const optionValue = lowestPriceVariant.options[index];
      const option = Array.from(select.options).find(opt => opt.value === optionValue);
      
      if (option && select.value !== optionValue) {
        select.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
  
  // Update the hidden variant ID input in the product form
  updateProductForms(lowestPriceVariant.id);
  
  // Update all condition/grade variant prices
  updateAllConditionPrices(variantData);
  
}

/**
 * Sets up event listeners for option changes
 */
function setupOptionChangeHandlers(variantSelects, variantData) {
  // Handle radio button inputs (button-style variant selectors)
  const radioInputs = variantSelects.querySelectorAll('input[type="radio"]');
  radioInputs.forEach(input => {
    input.addEventListener('change', () => {
      handleOptionChange(variantSelects, variantData);
    });
  });
  
  // Handle select elements (dropdown-style variant selectors)
  const selects = variantSelects.querySelectorAll('select');
  selects.forEach(select => {
    select.addEventListener('change', () => {
      handleOptionChange(variantSelects, variantData);
    });
  });
}

/**
 * Handles option changes and selects the first available variant if current combination is unavailable
 */
function handleOptionChange(variantSelects, variantData) {
  // Prevent infinite recursion
  if (window._isHandlingOptionChange) {
    return;
  }
  
  try {
    window._isHandlingOptionChange = true;
    
    // Get current selected options
    const selectedOptions = getCurrentSelectedOptions(variantSelects);
    
    // Find the variant that matches the selected options
    const currentVariant = findVariantByOptions(variantData, selectedOptions);
    
    // Update all condition/grade variant prices based on the current selection
    updateAllConditionPrices(variantData, selectedOptions);
    
    // Constants for option indexes - CORRECTED ORDER
    const CONDITION_OPTION_INDEX = 0;
    const COLOR_OPTION_INDEX = 1;
    const SIZE_OPTION_INDEX = 2;
    
    // Get the changed option index
    const changedOptionIndex = getChangedOptionIndex(variantSelects);
    
    // Check if the condition/grade option was changed
    const isConditionChange = changedOptionIndex === CONDITION_OPTION_INDEX;
    const isColorChange = changedOptionIndex === COLOR_OPTION_INDEX;
    const isSizeChange = changedOptionIndex === SIZE_OPTION_INDEX;
    
    // If condition was changed, we need to handle color availability specially
    if (isConditionChange) {
      handleConditionChange(variantSelects, variantData, selectedOptions);
      return;
    }
    
    // If the current combination is unavailable, find the first available variant
    if (!currentVariant || !currentVariant.available) {
      // Get the current values
      const currentCondition = selectedOptions[CONDITION_OPTION_INDEX];
      const currentColor = selectedOptions[COLOR_OPTION_INDEX];
      const currentSize = selectedOptions[SIZE_OPTION_INDEX];
      
      // Handle color option changes - try to keep the same size if possible
      if (isColorChange) {
        // First, check if there's an available variant with the current color and size
        const availableVariantWithCurrentSize = findVariantWithColorAndSize(
          variantData,
          currentCondition,
          currentColor,
          currentSize
        );
        
        if (availableVariantWithCurrentSize) {
          // Great! We can use the current size with the new color
          applyVariantOptions(variantSelects, availableVariantWithCurrentSize);
          updateProductForms(availableVariantWithCurrentSize.id);
        } else {
          // No variant available with current size, find first available size for this color
          const availableVariantForColor = findFirstAvailableVariantForColor(
            variantData,
            currentCondition,
            currentColor
          );
          
          if (availableVariantForColor) {
            applyVariantOptions(variantSelects, availableVariantForColor);
            updateProductForms(availableVariantForColor.id);
          }
        }
      }
      
      // Handle size option changes - try to keep the same color if possible
      else if (isSizeChange) {
        // First, check if there's an available variant with the current color and size
        const availableVariantWithCurrentColor = findVariantWithColorAndSize(
          variantData,
          currentCondition,
          currentColor,
          currentSize
        );
        
        if (availableVariantWithCurrentColor) {
          // Great! We can use the current color with the new size
          applyVariantOptions(variantSelects, availableVariantWithCurrentColor);
          updateProductForms(availableVariantWithCurrentColor.id);
        } else {
          // No variant available with current color, find first available color for this size
          const availableVariantForSize = findFirstAvailableVariantForSize(
            variantData,
            currentCondition,
            currentSize
          );
          
          if (availableVariantForSize) {
            applyVariantOptions(variantSelects, availableVariantForSize);
            updateProductForms(availableVariantForSize.id);
          }
        }
      }
    }
  } finally {
    // Always reset the flag when done
    window._isHandlingOptionChange = false;
  }
}

/**
 * Special handler for condition/grade changes to ensure color swatches are properly updated
 */
function handleConditionChange(variantSelects, variantData, selectedOptions) {
  // CORRECTED ORDER
  const CONDITION_OPTION_INDEX = 0;
  const COLOR_OPTION_INDEX = 1;
  const SIZE_OPTION_INDEX = 2;
  
  const currentCondition = selectedOptions[CONDITION_OPTION_INDEX];
  const currentColor = selectedOptions[COLOR_OPTION_INDEX];
  const currentSize = selectedOptions[SIZE_OPTION_INDEX];
  
  // Find the color fieldset which contains the color swatches
  const colorFieldset = findColorFieldset(variantSelects);
  if (!colorFieldset) {
    return;
  }
  
  // Try to keep both color and size if possible
  const variantWithCurrentColorAndSize = findVariantWithColorAndSize(
    variantData,
    currentCondition,
    currentColor,
    currentSize
  );
  
  if (variantWithCurrentColorAndSize) {
    // Great! We can keep both color and size
    safeApplyVariantOptions(variantSelects, variantWithCurrentColorAndSize);
    updateProductForms(variantWithCurrentColorAndSize.id);
    return;
  }
  
  // Try to keep the color and find a new size
  const variantWithCurrentColor = findFirstAvailableVariantForColor(
    variantData,
    currentCondition,
    currentColor
  );
  
  if (variantWithCurrentColor) {
    // We can keep the color but need to change the size
    safeApplyVariantOptions(variantSelects, variantWithCurrentColor);
    updateProductForms(variantWithCurrentColor.id);
    return;
  }
  
  // Try to keep the size and find a new color
  const variantWithCurrentSize = findFirstAvailableVariantForSize(
    variantData,
    currentCondition,
    currentSize
  );
  
  if (variantWithCurrentSize) {
    // We can keep the size but need to change the color
    const availableColor = variantWithCurrentSize.options[COLOR_OPTION_INDEX];
    
    // Find the input for the available color
    const availableInput = findColorInput(colorFieldset, availableColor);
    if (availableInput) {
      // Use our safer approach to update the UI
      safeUpdateColorSwatch(availableInput, variantWithCurrentSize.id);
      return;
    }
    
    // If we couldn't find the input, just apply the variant
    safeApplyVariantOptions(variantSelects, variantWithCurrentSize);
    updateProductForms(variantWithCurrentSize.id);
    return;
  }
  
  // Last resort - find any available variant with this condition
  const anyAvailableVariant = findAnyAvailableVariantWithCondition(
    variantData,
    CONDITION_OPTION_INDEX,
    currentCondition
  );
  
  if (anyAvailableVariant) {
    safeApplyVariantOptions(variantSelects, anyAvailableVariant);
    updateProductForms(anyAvailableVariant.id);
  }
}

/**
 * Safely updates a color swatch without triggering infinite recursion
 */
function safeUpdateColorSwatch(input, variantId) {
  // Set a flag to prevent recursive event handling
  window._isUpdatingColorSwatch = true;
  
  try {
    // Check the input
    input.checked = true;
    
    // Update the form directly instead of triggering events
    updateProductForms(variantId);
    
    // Use setTimeout to break the call stack
    setTimeout(() => {
      // Now trigger the change event
      input.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Find and click the label to update the UI
      const label = document.querySelector(`label[for="${input.id}"]`);
      if (label) {
        // Use another setTimeout to further break the call stack
        setTimeout(() => {
          label.click();
          window._isUpdatingColorSwatch = false;
        }, 0);
      } else {
        window._isUpdatingColorSwatch = false;
      }
    }, 0);
  } catch (error) {
    console.error('Error updating color swatch:', error);
    window._isUpdatingColorSwatch = false;
  }
}

/**
 * Safely applies variant options to prevent infinite recursion
 */
function safeApplyVariantOptions(variantSelects, variant) {
  // Set a flag to prevent recursive event handling
  window._isSafelyApplyingVariant = true;
  
  try {
    // Handle button-style variant selectors
    const fieldsets = variantSelects.querySelectorAll('fieldset');
    if (fieldsets.length > 0) {
      // Process each fieldset one by one with delays between them
      processFieldsetsSequentially(fieldsets, variant, 0);
    } else {
      // Handle dropdown-style variant selectors
      const selects = variantSelects.querySelectorAll('select');
      if (selects.length > 0) {
        // Process each select one by one with delays between them
        processSelectsSequentially(selects, variant, 0);
      }
    }
  } catch (error) {
    console.error('Error in safeApplyVariantOptions:', error);
    window._isSafelyApplyingVariant = false;
  }
}

/**
 * Process fieldsets one by one with delays to prevent stack overflow
 */
function processFieldsetsSequentially(fieldsets, variant, index) {
  if (index >= fieldsets.length) {
    // We're done, reset the flag
    setTimeout(() => {
      window._isSafelyApplyingVariant = false;
    }, 50);
    return;
  }
  
  const fieldset = fieldsets[index];
  const optionValue = variant.options[index];
  
  // First try to find the input by value
  let input = fieldset.querySelector(`input[value="${optionValue}"]`);
  
  // If not found, try to find by data attribute (for color swatches)
  if (!input) {
    const optionName = fieldset.querySelector('legend')?.textContent.split(':')[0].trim();
    if (optionName) {
      const labels = fieldset.querySelectorAll(`label[data-${optionName.toLowerCase()}="${optionValue}"]`);
      if (labels.length > 0) {
        const labelId = labels[0].getAttribute('for');
        if (labelId) {
          input = document.getElementById(labelId);
        }
      }
    }
  }
  
  // If we found the input, select it
  if (input && !input.checked) {
    input.checked = true;
    
    // Use setTimeout to break the call stack
    setTimeout(() => {
      // Only dispatch events if it's not a color swatch (those are handled specially)
      const isColorSwatch = input.classList.contains('color-swatch-input') || 
                           fieldset.classList.contains('color-fieldset') ||
                           fieldset.querySelector('.color-swatch');
      
      if (!isColorSwatch) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Process the next fieldset after a short delay
      setTimeout(() => {
        processFieldsetsSequentially(fieldsets, variant, index + 1);
      }, 10);
    }, 10);
  } else {
    // Skip to the next fieldset
    setTimeout(() => {
      processFieldsetsSequentially(fieldsets, variant, index + 1);
    }, 10);
  }
}

/**
 * Process select dropdowns one by one with delays to prevent stack overflow
 */
function processSelectsSequentially(selects, variant, index) {
  if (index >= selects.length) {
    // We're done, reset the flag
    setTimeout(() => {
      window._isSafelyApplyingVariant = false;
    }, 50);
    return;
  }
  
  const select = selects[index];
  const optionValue = variant.options[index];
  
  if (select.value !== optionValue) {
    select.value = optionValue;
    
    // Use setTimeout to break the call stack
    setTimeout(() => {
      select.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Process the next select after a short delay
      setTimeout(() => {
        processSelectsSequentially(selects, variant, index + 1);
      }, 10);
    }, 10);
  } else {
    // Skip to the next select
    setTimeout(() => {
      processSelectsSequentially(selects, variant, index + 1);
    }, 10);
  }
}

/**
 * Finds the fieldset that contains color options
 */
function findColorFieldset(variantSelects) {
  const fieldsets = variantSelects.querySelectorAll('fieldset');
  
  for (const fieldset of fieldsets) {
    const legend = fieldset.querySelector('legend');
    if (legend && legend.textContent.toLowerCase().includes('color')) {
      return fieldset;
    }
    
    // Check if this fieldset has color swatches
    if (fieldset.querySelector('.color-swatch')) {
      return fieldset;
    }
  }
  
  // If we can't find by legend text, try to find by position (usually the third option)
  if (fieldsets.length >= 3) {
    return fieldsets[2]; // Third option is often color
  }
  
  return null;
}

/**
 * Finds the input element for a specific color value
 */
function findColorInput(colorFieldset, colorValue) {
  // First try direct value match
  let input = colorFieldset.querySelector(`input[value="${colorValue}"]`);
  if (input && !input.classList.contains('disabled')) {
    return input;
  }
  
  // Try finding by data attribute
  const labels = colorFieldset.querySelectorAll(`label[data-color="${colorValue}"]`);
  if (labels.length > 0) {
    const labelId = labels[0].getAttribute('for');
    if (labelId) {
      input = document.getElementById(labelId);
      if (input && !input.classList.contains('disabled')) {
        return input;
      }
    }
  }
  
  // Try case-insensitive match
  const allInputs = colorFieldset.querySelectorAll('input');
  for (const input of allInputs) {
    if (input.value.toLowerCase() === colorValue.toLowerCase() && !input.classList.contains('disabled')) {
      return input;
    }
  }
  
  // Return the first non-disabled input as fallback
  return colorFieldset.querySelector('input:not(.disabled)');
}

/**
 * Checks if a color is available with the specified condition and size
 */
function isColorAvailableWithConditionAndSize(variantData, condition, color, size) {
  return variantData.some(variant => 
    variant.available && 
    variant.options[0] === condition &&
    variant.options[1] === color &&
    variant.options[2] === size
  );
}

/**
 * Finds the first available color for the specified condition and size
 */
function findFirstAvailableColorForConditionAndSize(variantData, condition, size) {
  const variant = variantData.find(variant => 
    variant.available && 
    variant.options[0] === condition &&
    variant.options[2] === size
  );
  
  return variant ? variant.options[1] : null;
}

/**
 * Updates all condition/grade variant prices based on current selection
 */
function updateAllConditionPrices(variantData, selectedOptions = null) {
  // Find all condition/grade options
  const conditionOptions = findAllConditionOptions(variantData);
  
  // Get current selected options (if not provided)
  if (!selectedOptions) {
    const variantSelects = document.querySelector('variant-selects');
    if (variantSelects) {
      selectedOptions = getCurrentSelectedOptions(variantSelects);
    } else {
      return;
    }
  }
  
  // Find which option index is for condition/grade (usually option1, but let's be sure)
  const conditionOptionIndex = findConditionOptionIndex(variantData);
  if (conditionOptionIndex === -1) return;

  // Build a mapping of condition names to their DOM elements
  const conditionPriceElementMap = buildConditionPriceElementMap(conditionOptions);
  
  // For each condition option, find the matching variant and update its price
  conditionOptions.forEach(conditionValue => {
    // Create a copy of selected options
    const optionsToCheck = [...selectedOptions];
    // Replace the condition option with the current condition we're checking
    optionsToCheck[conditionOptionIndex] = conditionValue;
    
    // Find the variant with these options - prioritize matching current color and size
    const variant = findBestMatchingVariant(variantData, optionsToCheck, conditionOptionIndex);
    
    if (variant && variant.available) {
      // Format the price correctly (Shopify prices are in cents/paise)
      // Convert to main currency unit and format
      const priceInMainUnit = variant.price / 100;
      
      // Format as number without decimals if it's a whole number
      // Otherwise format with proper locale formatting
      let formattedPrice;
      if (priceInMainUnit % 1 === 0) {
        // Whole number - format without decimals
        formattedPrice = Math.floor(priceInMainUnit).toLocaleString('en-IN');
      } else {
        // Has decimals - format with locale
        formattedPrice = priceInMainUnit.toLocaleString('en-IN', { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }
      
      // Find and update the price element for this condition
      updateConditionPriceElement(conditionValue, formattedPrice, conditionPriceElementMap);
    }
  });
}

/**
 * Finds all unique condition/grade options in the variant data
 */
function findAllConditionOptions(variantData) {
  const conditionOptionIndex = findConditionOptionIndex(variantData);
  if (conditionOptionIndex === -1) return [];
  
  // Get all unique condition values
  const conditionOptions = new Set();
  variantData.forEach(variant => {
    conditionOptions.add(variant.options[conditionOptionIndex]);
  });
  
  return Array.from(conditionOptions);
}

/**
 * Determines which option index corresponds to condition/grade
 */
function findConditionOptionIndex(variantData) {
  // Check if we have variants
  if (!variantData || variantData.length === 0) return -1;
  
  // Look for condition or grade in the option names
  const conditionNames = ['Condition', 'Grade'];
  
  // Try to find option elements to determine which one is condition/grade
  const variantSelects = document.querySelector('variant-selects');
  if (!variantSelects) return 0; // Default to first option
  
  const fieldsets = variantSelects.querySelectorAll('fieldset');
  for (let i = 0; i < fieldsets.length; i++) {
    const legend = fieldsets[i].querySelector('legend');
    if (legend) {
      const legendText = legend.textContent.trim();
      for (const conditionName of conditionNames) {
        if (legendText.includes(conditionName + ':')) {
          return i;
        }
      }
    }
  }
  
  // If we can't determine from the UI, check if any price elements exist with condition class
  if (document.querySelector('.variant-price-Excellent') || 
      document.querySelector('.variant-price-Good') || 
      document.querySelector('.variant-price-Fair') ||
      document.querySelector('.variant-price-Superb') ||
      document.querySelector('.variant-price-Very\\.Good')) {
    return 0; // Most likely the first option
  }
  
  return 0; // Default to first option if we can't determine
}

/**
 * Finds the best matching variant for the given options
 * Prioritizes matching the condition option and as many other options as possible
 * IMPORTANT: When showing prices for condition buttons, we want to show the price
 * that matches the current color and size selection, only changing the condition
 */
function findBestMatchingVariant(variantData, options, conditionOptionIndex) {
  // First try to find an exact match
  const exactMatch = findVariantByOptions(variantData, options);
  if (exactMatch && exactMatch.available) return exactMatch;
  
  // If no exact match, prioritize variants that match ALL non-condition options
  // This ensures we show the price for the same color/size combination
  const nonConditionIndexes = [];
  for (let i = 0; i < options.length; i++) {
    if (i !== conditionOptionIndex) {
      nonConditionIndexes.push(i);
    }
  }
  
  // Find variants that match the condition AND all other options
  const matchingConditionVariants = variantData.filter(variant => {
    if (!variant.available) return false;
    if (variant.options[conditionOptionIndex] !== options[conditionOptionIndex]) return false;
    
    // Check if all non-condition options match
    return nonConditionIndexes.every(index => variant.options[index] === options[index]);
  });
  
  // If we found variants matching all options (just condition changed), return the first one
  if (matchingConditionVariants.length > 0) {
    return matchingConditionVariants[0];
  }
  
  // If no perfect match, find variants that match the condition and prioritize matching options
  const conditionMatchingVariants = variantData.filter(variant => 
    variant.available && 
    variant.options[conditionOptionIndex] === options[conditionOptionIndex]
  );
  
  if (conditionMatchingVariants.length === 0) return null;
  
  // From those, find the one that matches the most other options (priority: exact matches)
  let bestMatch = conditionMatchingVariants[0];
  let maxMatchCount = 0;
  
  conditionMatchingVariants.forEach(variant => {
    let matchCount = 0;
    variant.options.forEach((optionValue, index) => {
      if (index !== conditionOptionIndex && optionValue === options[index]) {
        matchCount++;
      }
    });
    
    if (matchCount > maxMatchCount) {
      maxMatchCount = matchCount;
      bestMatch = variant;
    }
  });
  
  return bestMatch;
}

/**
 * Builds a mapping of condition names to their corresponding price elements in the DOM
 */
function buildConditionPriceElementMap(conditionOptions) {
  const map = new Map();
  
  // Find all elements with class starting with "variant-price-"
  const allPriceElements = document.querySelectorAll('[class*="variant-price-"]');
  
  // For each price element, try to determine which condition it corresponds to
  allPriceElements.forEach(element => {
    const classList = Array.from(element.classList);
    const priceClass = classList.find(cls => cls.startsWith('variant-price-'));
    
    if (priceClass) {
      // Extract condition name from the class
      let conditionName = priceClass.replace('variant-price-', '');
      
      // Handle special cases with dots
      conditionName = conditionName.replace(/\./g, ' ');
      
      // Check if this matches any of our known condition options
      const matchingCondition = conditionOptions.find(option => 
        option === conditionName || 
        option.replace(/ /g, '.') === conditionName ||
        option.replace(/ /g, '') === conditionName
      );
      
      if (matchingCondition) {
        map.set(matchingCondition, element);
      } else {
        // If no direct match, try to infer the condition name
        for (const option of conditionOptions) {
          if (conditionName.includes(option) || option.includes(conditionName)) {
            map.set(option, element);
            break;
          }
        }
      }
    }
  });

  
  return map;
}

/**
 * Updates the price display element for a specific condition
 */
function updateConditionPriceElement(conditionValue, price, conditionPriceElementMap = null) {
  // First try using the global mapping
  if (conditionPriceElementsMap.has(conditionValue)) {
    const priceElement = conditionPriceElementsMap.get(conditionValue);
    priceElement.innerHTML = '';
    priceElement.textContent = price;
    return;
  }
  
  // Then try using the provided mapping if available
  if (conditionPriceElementMap && conditionPriceElementMap.has(conditionValue)) {
    const priceElement = conditionPriceElementMap.get(conditionValue);
    priceElement.innerHTML = '';
    priceElement.textContent = price;
    return;
  }
  
  // Try to find the element using various selectors
  let priceElement = null;
  
  // Try with exact value (for single-word conditions like "Good")
  let selector = `.variant-price-${conditionValue}`;
  priceElement = document.querySelector(selector);
  
  // If not found and condition has spaces (like "Very Good"), handle space encoding
  if (!priceElement && conditionValue.includes(' ')) {
    // Try with space encoded as dot (most common in CSS class names)
    selector = `.variant-price-${conditionValue.replace(/ /g, '.')}`;
    priceElement = document.querySelector(selector);
    
    // If not found, try with escaped dots
    if (!priceElement) {
      selector = `.variant-price-${conditionValue.replace(/ /g, '\\.')}`;
      priceElement = document.querySelector(selector);
    }
    
    // If still not found, try with hyphens (alternative encoding)
    if (!priceElement) {
      selector = `.variant-price-${conditionValue.replace(/ /g, '-')}`;
      priceElement = document.querySelector(selector);
    }
    
    // Try matching first word only (in case of split classes: variant-price-Very and Good)
    if (!priceElement) {
      const firstWord = conditionValue.split(' ')[0];
      selector = `.variant-price-${firstWord}`;
      const elements = document.querySelectorAll(selector);
      // Check if element also has the second word as a class
      for (const el of elements) {
        const secondWord = conditionValue.split(' ')[1];
        if (secondWord && el.classList.contains(secondWord)) {
          priceElement = el;
          break;
        }
      }
    }
  }
  
  // If not found, try with escaped periods (for existing code compatibility)
  if (!priceElement && conditionValue.includes('.')) {
    selector = `.variant-price-${conditionValue.replace(/\./g, '\\.')}`;
    priceElement = document.querySelector(selector);
  }
  
  // Special case handling for common condition names
  if (!priceElement) {
    const specialCases = {
      'Very Good': '.variant-price-Very\\.Good',
      'Like New': '.variant-price-Like\\.New',
      'Brand New': '.variant-price-Brand\\.New',
      'Good as New': '.variant-price-Good\\.as\\.New',
      'Factory Sealed': '.variant-price-Factory\\.Sealed'
    };
    
    if (specialCases[conditionValue]) {
      priceElement = document.querySelector(specialCases[conditionValue]);
    }
  }
  
  // If we found the element, update its price and add to global map
  if (priceElement) {
    priceElement.innerHTML = '';
    priceElement.textContent = price;
    
    // Add to global mapping for future use
    conditionPriceElementsMap.set(conditionValue, priceElement);
  } else {
    
    // Try a more aggressive search for elements containing the variant-price class
    const allPriceElements = document.querySelectorAll('*[class*="variant-price"]');
    
    // Try to find a close match
    for (const element of allPriceElements) {
      const className = element.className;
      if (className.includes(conditionValue) || 
          className.includes(conditionValue.replace(/ /g, '.')) ||
          className.includes(conditionValue.replace(/ /g, '-'))) {
        element.innerHTML = '';
        element.textContent = price;
        
        // Add to global mapping for future use
        conditionPriceElementsMap.set(conditionValue, element);
        return;
      }
    }
  }
}

/**
 * Gets the currently selected options from the variant selectors
 */
function getCurrentSelectedOptions(variantSelects) {
  const options = [];
  
  // Check for fieldsets with radio buttons
  const fieldsets = variantSelects.querySelectorAll('fieldset');
  if (fieldsets.length > 0) {
    fieldsets.forEach(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput) {
        options.push(checkedInput.value);
      }
    });
  }
  
  // Check for select dropdowns
  const selects = variantSelects.querySelectorAll('select');
  if (selects.length > 0) {
    selects.forEach(select => {
      options.push(select.value);
    });
  }
  
  return options;
}

/**
 * Finds a variant by its options
 */
function findVariantByOptions(variantData, options) {
  return variantData.find(variant => {
    return variant.options.every((option, index) => option === options[index]);
  });
}

/**
 * Gets the index of the option that was just changed
 * This is a heuristic that assumes the most recently focused element was changed
 */
function getChangedOptionIndex(variantSelects) {
  // For radio buttons
  const fieldsets = variantSelects.querySelectorAll('fieldset');
  for (let i = 0; i < fieldsets.length; i++) {
    const checkedInput = fieldsets[i].querySelector('input:checked:focus');
    if (checkedInput) {
      return i;
    }
  }
  
  // For select dropdowns
  const selects = variantSelects.querySelectorAll('select');
  for (let i = 0; i < selects.length; i++) {
    if (selects[i] === document.activeElement) {
      return i;
    }
  }
  
  // If we can't determine which option was changed, return the first option
  return 0;
}

/**
 * Finds the first available variant with the specified size value
 * that keeps the condition option unchanged
 */
function findFirstAvailableVariantWithSize(variantData, sizeOptionIndex, sizeValue, currentOptions) {
  // We assume condition is always the first option (index 0)
  const CONDITION_OPTION_INDEX = 0;
  const currentCondition = currentOptions[CONDITION_OPTION_INDEX];
  
  // Find an available variant with the same size and condition
  return variantData.find(variant => 
    variant.available && 
    variant.options[sizeOptionIndex] === sizeValue &&
    variant.options[CONDITION_OPTION_INDEX] === currentCondition
  );
}

/**
 * Finds the first available variant that preserves the condition and the changed option value
 */
function findFirstAvailableVariantPreservingCondition(
  variantData, 
  conditionOptionIndex, 
  conditionValue,
  changedOptionIndex,
  changedOptionValue
) {
  // Find an available variant with the same condition and changed option value
  return variantData.find(variant => 
    variant.available && 
    variant.options[conditionOptionIndex] === conditionValue &&
    variant.options[changedOptionIndex] === changedOptionValue
  );
}

/**
 * Finds any available variant with the specified condition and another option value
 */
function findAnyAvailableVariantWithConditionAndSize(
  variantData,
  conditionOptionIndex,
  conditionValue,
  secondOptionIndex,
  secondOptionValue
) {
  // Find an available variant with the same condition and second option value
  return variantData.find(variant => 
    variant.available && 
    variant.options[conditionOptionIndex] === conditionValue &&
    variant.options[secondOptionIndex] === secondOptionValue
  );
}

/**
 * Finds any available variant with the specified condition
 */
function findAnyAvailableVariantWithCondition(
  variantData,
  conditionOptionIndex,
  conditionValue
) {
  // Find any available variant with the same condition
  return variantData.find(variant => 
    variant.available && 
    variant.options[conditionOptionIndex] === conditionValue
  );
}

/**
 * Applies the variant options to the UI
 */
function applyVariantOptions(variantSelects, variant) {
  // Handle button-style variant selectors
  const fieldsets = variantSelects.querySelectorAll('fieldset');
  if (fieldsets.length > 0) {
    fieldsets.forEach((fieldset, index) => {
      const optionValue = variant.options[index];
      
      // First try to find the input by value
      let input = fieldset.querySelector(`input[value="${optionValue}"]`);
      
      // If not found, try to find by data attribute (for color swatches)
      if (!input) {
        const optionName = fieldset.querySelector('legend')?.textContent.split(':')[0].trim();
        if (optionName) {
          const labels = fieldset.querySelectorAll(`label[data-${optionName.toLowerCase()}="${optionValue}"]`);
          if (labels.length > 0) {
            const labelId = labels[0].getAttribute('for');
            if (labelId) {
              input = document.getElementById(labelId);
            }
          }
        }
      }
      
      // If we found the input, select it
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Also trigger click on the label for color swatches
        const label = document.querySelector(`label[for="${input.id}"]`);
        if (label) {
          label.click();
        }
      }
    });
  }
  
  // Handle dropdown-style variant selectors
  const selects = variantSelects.querySelectorAll('select');
  if (selects.length > 0) {
    selects.forEach((select, index) => {
      const optionValue = variant.options[index];
      if (select.value !== optionValue) {
        select.value = optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }
}

/**
 * Updates the hidden variant ID inputs in all product forms
 */
function updateProductForms(variantId) {
  const productForms = document.querySelectorAll(`form[data-type="add-to-cart-form"]`);
  productForms.forEach(form => {
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput && variantInput.value !== variantId.toString()) {
      variantInput.value = variantId.toString();
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
} 

/**
 * Finds a variant with the specified condition, color, and size
 */
function findVariantWithColorAndSize(variantData, condition, color, size) {
  return variantData.find(variant => 
    variant.available && 
    variant.options[0] === condition &&
    variant.options[1] === color &&
    variant.options[2] === size
  );
}

/**
 * Finds the first available variant for a specific color
 */
function findFirstAvailableVariantForColor(variantData, condition, color) {
  return variantData.find(variant => 
    variant.available && 
    variant.options[0] === condition &&
    variant.options[1] === color
  );
}

/**
 * Finds the first available variant for a specific size
 */
function findFirstAvailableVariantForSize(variantData, condition, size) {
  return variantData.find(variant => 
    variant.available && 
    variant.options[0] === condition &&
    variant.options[2] === size
  );
} 